import { useEffect, useState } from 'react';
import { Copy, Zap, Smartphone, Whatsapp, X } from '@/components/icons';
import { createPortal } from 'react-dom';
import { useMutation } from '@tanstack/react-query';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { FormGroup } from '@/components/form/FormGroup';
import { useToast } from '@/components/ui/useToast';
import { generatePortalToken, type Rfq } from '@/api/rfq';
import { sendPortalLinkWhatsApp, getNotificationConfig } from '@/api/admin';
import { getParty } from '@/api/parties';
import { apiError } from '@/lib/apiError';
import { formatDate } from '@/lib/format';

interface Props {
  open: boolean;
  rfq: Rfq | null;
  onClose: () => void;
}

interface VendorTokenState {
  token: string;
  expiresAt: string;
}

interface WaDialogState {
  vendorId: number;
  vendorName: string;
  phone: string;
  token: string;
  message: string;
}

function buildMessage(vendorName: string, rfqNo: string, portalUrl: string): string {
  return `Dear ${vendorName} (Sales Team),

I am writing on behalf of Grhamoy to formally request a quotation. We are looking to stock up our inventory and believe your company's offerings may align well with our needs.

Please review our requirements below and provide a comprehensive quote:

📋 RFQ Reference: ${rfqNo}

🔗 Submit your quote here (secure, single-use link):
${portalUrl}

This link expires in 72 hours. Kindly submit at the earliest.

Thank you for your time and support.

Warm regards,
Gauvin's Home Pvt Ltd (Grhamoy)`;
}

export function PortalLinkModal({ open, rfq, onClose }: Props) {
  const toast = useToast();
  const [tokens, setTokens] = useState<Record<number, VendorTokenState>>({});
  const [waSending, setWaSending] = useState<Record<number, boolean>>({});
  const [vendorPhones, setVendorPhones] = useState<Record<number, string | null>>({});
  const [waDialog, setWaDialog] = useState<WaDialogState | null>(null);
  const [waConnected, setWaConnected] = useState(false);

  useEffect(() => {
    if (!open) { setTokens({}); setWaSending({}); setVendorPhones({}); setWaDialog(null); return; }
    // WhatsApp is available whenever the BusinessOnBot config is saved
    // (API key + endpoint). Fetch once — no ongoing session concept.
    getNotificationConfig()
      .then((cfg) => setWaConnected(!!cfg.bonbApiKeySet && !!cfg.bonbEndpoint))
      .catch(() => setWaConnected(false));
  }, [open]);

  useEffect(() => {
    if (!open || !rfq) return;
    rfq.vendors.forEach((rv) => {
      const cached = rv.vendor.whatsapp ?? rv.vendor.mobile;
      if (!cached) {
        getParty(rv.vendor.id)
          .then((p) => {
            const phone = p.whatsapp ?? p.mobile;
            setVendorPhones((s) => ({ ...s, [rv.vendor.id]: phone ?? null }));
          })
          .catch(() => setVendorPhones((s) => ({ ...s, [rv.vendor.id]: null })));
      } else {
        setVendorPhones((s) => ({ ...s, [rv.vendor.id]: cached }));
      }
    });
  }, [open, rfq]);

  const mutation = useMutation({
    mutationFn: ({ vendorId }: { vendorId: number }) =>
      generatePortalToken(rfq!.id, vendorId).then((res) => ({ vendorId, ...res })),
    onSuccess: ({ vendorId, token, expiresAt }) => {
      setTokens((s) => ({ ...s, [vendorId]: { token, expiresAt } }));
    },
    onError: (e) => toast.push(apiError(e, 'Could not generate link'), 'error'),
  });

  const vendors = rfq?.vendors ?? [];
  const pendingVendors = vendors.filter((rv) => !tokens[rv.vendor.id]);
  const allGenerated = vendors.length > 0 && pendingVendors.length === 0;

  async function generateAll() {
    for (const rv of vendors.filter((rv) => !tokens[rv.vendor.id])) {
      mutation.mutate({ vendorId: rv.vendor.id });
    }
  }

  function buildUrl(token: string): string {
    return `${window.location.origin}/portal/quote/${token}`;
  }

  function copyLink(token: string) {
    void navigator.clipboard.writeText(buildUrl(token)).then(
      () => toast.push('Link copied to clipboard', 'success'),
      () => toast.push('Copy failed — select the link manually', 'error'),
    );
  }

  function copyAll() {
    const lines = vendors
      .map((rv) => { const t = tokens[rv.vendor.id]; return t ? `${rv.vendor.name}: ${buildUrl(t.token)}` : null; })
      .filter(Boolean).join('\n');
    void navigator.clipboard.writeText(lines).then(
      () => toast.push('All links copied to clipboard', 'success'),
      () => toast.push('Copy failed', 'error'),
    );
  }

  function openWaDialog(vendorId: number, token: string, vendorName: string) {
    const phone = vendorPhones[vendorId];
    setWaDialog({
      vendorId,
      vendorName,
      phone: phone ?? '',
      token,
      message: buildMessage(vendorName, rfq?.rfqNo ?? '', buildUrl(token)),
    });
  }

  async function sendDirect() {
    if (!waDialog) return;
    const { vendorId, token, message } = waDialog;
    setWaDialog(null);
    setWaSending((s) => ({ ...s, [vendorId]: true }));
    try {
      const result = await sendPortalLinkWhatsApp(vendorId, buildUrl(token), rfq?.rfqNo ?? '', message);
      toast.push(`Sent to ${result.phone} via WhatsApp`, 'success');
    } catch (err: unknown) {
      toast.push(apiError(err, 'Failed'), 'error');
    } finally {
      setWaSending((s) => ({ ...s, [vendorId]: false }));
    }
  }

  function shareAllWhatsapp() {
    vendors.forEach((rv) => {
      const t = tokens[rv.vendor.id];
      if (t) openWaDialog(rv.vendor.id, t.token, rv.vendor.name);
    });
  }

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        title={`Vendor Portal Links — ${rfq?.rfqNo ?? ''}`}
        size="lg"
        footer={<Button variant="glass" onClick={onClose}>Close</Button>}
      >
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div className="text-sm text-[var(--text-secondary)]">
            Generate a one-time, expiring link for each invited vendor. Share via WhatsApp / email.
          </div>
          <div className="flex gap-2">
            {allGenerated ? (
              <>
                <Button variant="glass" size="sm" onClick={copyAll}><Copy size={12} className="mr-1 inline-block align-[-2px]" /> Copy All</Button>
                <Button variant="success" size="sm" onClick={shareAllWhatsapp}>WhatsApp All</Button>
              </>
            ) : (
              <Button variant="primary" size="sm" onClick={generateAll} disabled={mutation.isPending || pendingVendors.length === 0}>
                <Zap size={14} className="mr-1 inline-block align-[-2px]" /> Generate All ({pendingVendors.length})
              </Button>
            )}
          </div>
        </div>

        <div className="space-y-2">
          {vendors.map((rv) => {
            const t = tokens[rv.vendor.id];
            const phone = vendorPhones[rv.vendor.id];
            const phoneFetched = rv.vendor.id in vendorPhones;
            return (
              <div key={rv.vendor.id} className="flex flex-wrap items-center gap-3 rounded-card border border-[var(--border-color)] bg-white/[0.02] px-3.5 py-2.5">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-bold text-[var(--text-primary)]">{rv.vendor.name}</div>
                  {phone ? (
                    <div className="text-[10px] text-[var(--text-muted)]"><Smartphone size={11} className="mr-1 inline-block align-[-1px]" /> {phone}</div>
                  ) : phoneFetched ? (
                    <div className="text-[10px] text-warning">No WhatsApp/mobile in Party Master</div>
                  ) : (
                    <div className="text-[10px] text-[var(--text-muted)]">Fetching number…</div>
                  )}
                  {t && (
                    <div className="mt-1 truncate text-[11px] font-mono text-[var(--text-muted)]">{buildUrl(t.token)}</div>
                  )}
                </div>
                {t ? (
                  <div className="flex gap-1.5">
                    <Button variant="glass" size="sm" onClick={() => copyLink(t.token)}><Copy size={12} className="mr-1 inline-block align-[-2px]" /> Copy</Button>
                    <Button
                      variant="success"
                      size="sm"
                      onClick={() => openWaDialog(rv.vendor.id, t.token, rv.vendor.name)}
                      disabled={waSending[rv.vendor.id] || !phoneFetched}
                      title={!phone && phoneFetched ? 'No WhatsApp number in Party Master' : 'Send via WhatsApp'}
                    >
                      {waSending[rv.vendor.id] ? 'Sending…' : 'WhatsApp'}
                    </Button>
                    <span className="self-center text-[11px] text-[var(--text-muted)]">Expires {formatDate(t.expiresAt)}</span>
                  </div>
                ) : (
                  <Button variant="primary" size="sm" onClick={() => mutation.mutate({ vendorId: rv.vendor.id })} disabled={mutation.isPending}>
                    Generate Link
                  </Button>
                )}
              </div>
            );
          })}
        </div>

        <FormGroup label="" className="mt-4">
          <div className="rounded-md border border-info/30 bg-info/[0.05] px-3 py-2 text-xs text-[var(--text-secondary)]">
            <strong className="text-info">Note:</strong> Each link is single-use and expires per the
            Vendor Portal TTL config (default 72 h). Generating a new link invalidates the previous one
            for that vendor only after submission.
          </div>
        </FormGroup>
      </Modal>

      {/* WhatsApp confirmation dialog — rendered at body root to sit above the Modal (z-500) */}
      {waDialog && createPortal(
        <div className="fixed inset-0 z-[600] flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-lg rounded-2xl border border-[var(--border-color)] bg-[var(--bg-secondary)] shadow-2xl">
            {/* Header */}
            <div className="flex items-center gap-3 border-b border-[var(--border-color)] px-5 py-4">
              <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-[#25D366]/20 text-lg"><Whatsapp size={18} className="text-[#25D366]" /></div>
              <div>
                <div className="font-extrabold text-[var(--text-primary)]">Send via WhatsApp</div>
                <div className="text-xs text-[var(--text-muted)]">{waDialog.vendorName}</div>
              </div>
              <button onClick={() => setWaDialog(null)} className="ml-auto text-[var(--text-muted)] hover:text-[var(--text-primary)]"><X size={14} /></button>
            </div>

            {/* Phone row */}
            <div className="px-5 pt-4">
              <div className="flex items-center gap-2 rounded-lg border border-[var(--border-color)] bg-[var(--bg-soft,var(--bg-secondary))] px-3 py-2">
                <span className="text-xs font-bold text-[var(--text-muted)] uppercase tracking-wide">To</span>
                {waDialog.phone ? (
                  <span className="text-sm font-bold text-[var(--text-primary)]"><Smartphone size={13} className="mr-1 inline-block align-[-2px]" /> {waDialog.phone}</span>
                ) : (
                  <span className="text-sm text-warning">No number in Party Master — will try server send</span>
                )}
                <span className={`ml-auto rounded-full px-2 py-0.5 text-[10px] font-bold ${
                  waConnected ? 'bg-[#25D366]/20 text-[#25D366]' : 'bg-[var(--border-color)] text-[var(--text-muted)]'
                }`}>
                  {waConnected ? '● Connected' : '○ Not connected'}
                </span>
              </div>
            </div>

            {/* Editable message */}
            <div className="px-5 pt-3">
              <label className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-[var(--text-muted)]">Message</label>
              <textarea
                rows={11}
                value={waDialog.message}
                onChange={(e) => setWaDialog((d) => d ? { ...d, message: e.target.value } : d)}
                className="w-full rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] px-3 py-2 text-xs leading-relaxed text-[var(--text-primary)] outline-none focus:border-[#25D366] resize-none"
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 px-5 py-4">
              <button onClick={() => setWaDialog(null)} className="rounded-lg border border-[var(--border-color)] px-4 py-1.5 text-sm text-[var(--text-secondary)] hover:bg-white/[0.06] transition-colors">
                Cancel
              </button>
              <button
                onClick={() => void sendDirect()}
                className="flex items-center gap-2 rounded-lg bg-[#25D366] px-5 py-1.5 text-sm font-bold text-white hover:bg-[#1ebe5d] transition-colors"
                title={waConnected ? 'Send via QR session' : 'Send via WhatsApp API'}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                Send via WhatsApp
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
